/**
 * Bracket sub-lists: [...] → nested <ul> under the relevant list item.
 * Inside a single-bracket group, [[...]] becomes a third-level nested <ul> (sublist of sublist).
 * 1) List item is only [brackets] → merge as sub-bullets under the previous <li>
 * 2) Any top-level <li> with inline [a][b] after text → split into nested <ul>
 * 3) Standalone <p> lines that are only [brackets] → nest under last <li> of preceding list
 *
 * Used by `<custom-rich-text-root>` and `<faq-answer-rte>` (Faq.liquid loads this script).
 */

function normalizeSpaces(s) {
  return s.replace(/\u00a0/g, ' ');
}

/** @returns {number} index of the second `]` of `]]`, or -1 */
function findDoubleBracketClose(s, openIdx) {
  if (s[openIdx] !== '[' || s[openIdx + 1] !== '[') return -1;
  let j = openIdx + 2;
  while (j < s.length - 1) {
    if (s[j] === ']' && s[j + 1] === ']') return j + 1;
    j += 1;
  }
  return -1;
}

/** Matching `]` for a `[` that opens a single-bracket group; `[[...]]` is skipped as one unit. */
function findSingleBracketClose(s, openIdx) {
  if (s[openIdx] !== '[') return -1;
  if (s[openIdx + 1] === '[') return -1;
  let depth = 1;
  let i = openIdx + 1;
  while (i < s.length) {
    if (s[i] === '[' && s[i + 1] === '[') {
      const end = findDoubleBracketClose(s, i);
      if (end === -1) return -1;
      i = end + 1;
      continue;
    }
    if (s[i] === '[') {
      depth += 1;
      i += 1;
      continue;
    }
    if (s[i] === ']') {
      depth -= 1;
      if (depth === 0) return i;
      i += 1;
      continue;
    }
    i += 1;
  }
  return -1;
}

/**
 * Content inside one `[ ... ]` pair: text before first `[[` is the L2 label; each `[[x]]` is an L3 bullet.
 * @returns {{ prefix: string, subItems: string[] }}
 */
function parseLevel2Inner(inner) {
  const t = normalizeSpaces(inner);
  const subItems = [];
  const firstDouble = t.indexOf('[[');
  if (firstDouble === -1) {
    return { prefix: t.trim(), subItems: [] };
  }
  let prefix = t.slice(0, firstDouble).trimEnd();
  let pos = firstDouble;
  while (pos < t.length) {
    if (t[pos] === '[' && t[pos + 1] === '[') {
      const endClose = findDoubleBracketClose(t, pos);
      if (endClose === -1) break;
      const deepText = t.slice(pos + 2, endClose - 1).trim();
      if (deepText) subItems.push(deepText);
      pos = endClose + 1;
      continue;
    }
    pos += 1;
  }
  return { prefix: prefix.trimEnd(), subItems };
}

/**
 * Parse a run of top-level `[...][...]` starting at `from` (must point at `[` not `[[`).
 * @returns {{ endIndex: number, items: { prefix: string, subItems: string[] }[] }}
 */
function parseAllBracketGroups(s, from) {
  const items = [];
  let i = from;
  while (i < s.length) {
    if (s[i] !== '[') break;
    if (s[i + 1] === '[') break;
    const close = findSingleBracketClose(s, i);
    if (close === -1) break;
    const inner = s.slice(i + 1, close);
    items.push(parseLevel2Inner(inner));
    i = close + 1;
  }
  return { endIndex: i, items };
}

function buildUlFromParsedItems(items) {
  const ul = document.createElement('ul');
  for (const { prefix, subItems } of items) {
    const li = document.createElement('li');
    if (prefix) li.appendChild(document.createTextNode(prefix));
    if (subItems.length) {
      const deep = document.createElement('ul');
      for (const txt of subItems) {
        const li3 = document.createElement('li');
        li3.textContent = txt;
        deep.appendChild(li3);
      }
      li.appendChild(deep);
    }
    ul.appendChild(li);
  }
  return ul;
}

function appendParsedItemsToUl(targetUl, items) {
  for (const item of items) {
    const li = document.createElement('li');
    if (item.prefix) li.appendChild(document.createTextNode(item.prefix));
    if (item.subItems.length) {
      const deep = document.createElement('ul');
      for (const txt of item.subItems) {
        const li3 = document.createElement('li');
        li3.textContent = txt;
        deep.appendChild(li3);
      }
      li.appendChild(deep);
    }
    targetUl.appendChild(li);
  }
}

function isBracketOnlyText(text) {
  const t = normalizeSpaces(text).trim();
  if (!t || t[0] !== '[') return false;
  const parsed = parseAllBracketGroups(t, 0);
  if (!parsed.items.length) return false;
  return normalizeSpaces(t.slice(parsed.endIndex)).trim() === '';
}

function isTopLevelUl(ul) {
  return !(ul.parentElement && ul.parentElement.tagName === 'LI');
}

/** <li> whose entire text is only [x][y] → nest under previous sibling <li> */
function mergeBracketOnlyListItems(rte) {
  for (let pass = 0; pass < 30; pass += 1) {
    let merged = false;
    outer:
    for (const ul of rte.querySelectorAll('ul')) {
      if (!isTopLevelUl(ul)) continue;
      const items = [...ul.querySelectorAll(':scope > li')];
      for (let i = 1; i < items.length; i += 1) {
        const li = items[i];
        if (!isBracketOnlyText(li.textContent)) continue;
        const prev = items[i - 1];
        const t = normalizeSpaces(li.textContent).trim();
        const parsed = parseAllBracketGroups(t, 0);
        if (!parsed.items.length) continue;
        li.remove();
        let sub = prev.querySelector(':scope > ul');
        if (!sub) {
          sub = document.createElement('ul');
          prev.appendChild(sub);
        }
        appendParsedItemsToUl(sub, parsed.items);
        merged = true;
        break outer;
      }
    }
    if (!merged) break;
  }
}

/** Every direct <li> of a top-level <ul> that contains inline brackets after text */
function splitInlineBracketsInAllListItems(rte) {
  rte.querySelectorAll('ul').forEach((ul) => {
    if (!isTopLevelUl(ul)) return;

    for (const li of ul.querySelectorAll(':scope > li')) {
      if (!li.textContent.includes('[')) continue;

      const full = normalizeSpaces(li.textContent);
      const firstBracket = full.indexOf('[');
      if (firstBracket === -1) continue;
      if (full[firstBracket + 1] === '[') continue;

      const tail = full.slice(firstBracket);
      const parsed = parseAllBracketGroups(tail, 0);
      if (!parsed.items.length) continue;

      const prefix = full.slice(0, firstBracket).trimEnd();
      const suffix = normalizeSpaces(tail.slice(parsed.endIndex)).trimStart();

      li.replaceChildren();
      if (prefix) li.appendChild(document.createTextNode(prefix));
      li.appendChild(buildUlFromParsedItems(parsed.items));
      if (suffix) li.appendChild(document.createTextNode(suffix));
    }
  });
}

function findPrecedingList(fromEl) {
  let prev = fromEl.previousElementSibling;
  while (prev) {
    if (prev.tagName === 'UL' || prev.tagName === 'OL') return prev;
    if (prev.tagName === 'DIV') {
      const inner = prev.querySelector(':scope > ul, :scope > ol');
      if (inner) return inner;
    }
    prev = prev.previousElementSibling;
  }
  return null;
}

function promoteBracketOnlyParagraphs(rte) {
  for (let pass = 0; pass < 30; pass += 1) {
    const kids = Array.from(rte.children);
    let moved = false;
    for (let i = 0; i < kids.length; i += 1) {
      const el = kids[i];
      if (el.tagName !== 'P' || !isBracketOnlyText(el.textContent)) continue;

      const run = [el];
      let j = i + 1;
      while (j < kids.length && kids[j].tagName === 'P' && isBracketOnlyText(kids[j].textContent)) {
        run.push(kids[j]);
        j += 1;
      }

      const listEl = findPrecedingList(run[0]);
      if (!listEl) break;

      const lastLi = listEl.querySelector(':scope > li:last-child');
      if (!lastLi) break;

      const sub = document.createElement('ul');
      for (const p of run) {
        const t = normalizeSpaces(p.textContent).trim();
        const parsed = parseAllBracketGroups(t, 0);
        appendParsedItemsToUl(sub, parsed.items);
        p.remove();
      }
      lastLi.appendChild(sub);
      moved = true;
      break;
    }
    if (!moved) break;
  }
}

/**
 * Shared by custom rich text and FAQ answers: `[sub]` / `[Label:[[nested]]]` bracket syntax.
 * @param {HTMLElement | null} rte
 * @param {{ skipIfPlaceholder?: boolean }} [options]
 */
function transformBracketSubBulletsInRte(rte, options = {}) {
  if (!rte) return;
  if (options.skipIfPlaceholder && rte.classList.contains('custom-rich-text__body--placeholder')) return;
  mergeBracketOnlyListItems(rte);
  splitInlineBracketsInAllListItems(rte);
  promoteBracketOnlyParagraphs(rte);
}

if (!customElements.get('custom-rich-text-root')) {
  customElements.define(
    'custom-rich-text-root',
    class extends HTMLElement {
      connectedCallback() {
        const rte = this.querySelector('.custom-rich-text__body.rte');
        transformBracketSubBulletsInRte(rte, { skipIfPlaceholder: true });
      }
    }
  );
}

if (!customElements.get('faq-answer-rte')) {
  customElements.define(
    'faq-answer-rte',
    class extends HTMLElement {
      connectedCallback() {
        transformBracketSubBulletsInRte(this);
      }
    }
  );
}
